import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { FaEnvelope, FaLock } from "react-icons/fa";
import WebsiteNavbar from "../components/WebsiteNavbar";
import {
  login,
  verifyLoginOtp,
  resendLoginOtp,
  googleLogin,
  isLoggedIn,
} from "../services/authService";
import "../style/auth.css";
import loginImage from "../images/loginImage.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(1); // 1: credentials, 2: OTP
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    location.state?.registered
      ? "Account created successfully! Please sign in with your credentials."
      : ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  // 6-digit OTP state
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);
  const [resendTimer, setResendTimer] = useState(0);

  // If already logged in, go straight to dashboard
  useEffect(() => {
    if (isLoggedIn()) {
      navigate("/my-account", { replace: true });
    }
  }, [navigate]);

  // Pre-fill remembered email or email from signup
  useEffect(() => {
    if (location.state?.email) {
      setForm((prev) => ({ ...prev, email: location.state.email }));
    } else {
      const savedEmail = localStorage.getItem("remember_email");
      if (savedEmail) {
        setForm((prev) => ({ ...prev, email: savedEmail }));
        setRememberMe(true);
      }
    }
  }, [location.state]);

  // Resend OTP countdown timer
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError("");
    try {
      const result = await googleLogin(credentialResponse.credential);
      if (result.success) {
        const redirect = sessionStorage.getItem("redirect_after_login");
        sessionStorage.removeItem("redirect_after_login");
        navigate(redirect || "/my-account");
      } else {
        setError(result.error || "Google login failed.");
      }
    } catch (err) {
      setError("Google login failed. Please try again.");
    }
  };

  // Step 1: Submit email & password
  const handleCredentialSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!form.email || !form.password) {
      setError("Please enter both email and password.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await login(form.email, form.password);

      if (result.success) {
        if (rememberMe) {
          localStorage.setItem("remember_email", form.email);
        } else {
          localStorage.removeItem("remember_email");
        }

        if (result.status === "otp_required") {
          setStep(2);
          setResendTimer(60);
          setOtpDigits(["", "", "", "", "", ""]);
          setTimeout(() => otpRefs.current[0]?.focus(), 150);
        } else {
          const redirect = sessionStorage.getItem("redirect_after_login");
          sessionStorage.removeItem("redirect_after_login");
          navigate(redirect || "/my-account");
        }
      } else {
        setError(result.error || "Invalid email or password.");
      }
    } catch (err) {
      setError("Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2: OTP input handlers
  const handleOtpChange = (index, value) => {
    if (value && !/^[0-9]$/.test(value)) return;
    const updated = [...otpDigits];
    updated[index] = value;
    setOtpDigits(updated);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const updated = pasted.split("");
    while (updated.length < 6) updated.push("");
    setOtpDigits(updated);
    otpRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    setError("");
    const otp = otpDigits.join("");
    if (otp.length !== 6) {
      setError("Please enter all 6 digits of the verification code.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await verifyLoginOtp(form.email.trim(), otp);
      if (result.success) {
        const redirect = sessionStorage.getItem("redirect_after_login");
        sessionStorage.removeItem("redirect_after_login");
        navigate(redirect || "/my-account");
      } else {
        setError(result.error || "Verification failed. Please check the code and try again.");
      }
    } catch (err) {
      setError("Verification failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setError("");
    setOtpDigits(["", "", "", "", "", ""]);
    setResending(true);
    try {
      const result = await resendLoginOtp(form.email.trim());
      if (result.success) {
        setResendTimer(60);
        setSuccessMessage("A new verification code has been sent to your email.");
      } else {
        setError(result.error || "Failed to resend code.");
      }
    } catch (err) {
      setError("Failed to resend code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <WebsiteNavbar />

      <div className="auth-page">
        <div className="auth-card">
          {/* LEFT SIDE */}
          <div className="auth-left">
            {step === 1 ? (
              <>
                <h1 className="auth-title">
                  Welcome <span className="auth-accent">Back!</span>
                </h1>

                <p className="auth-subtitle">
                  Sign in to manage your skip bin bookings, view invoices and track hires.
                </p>

                {successMessage && <div className="auth-success">{successMessage}</div>}
                {error && <div className="auth-error">{error}</div>}

                <form onSubmit={handleCredentialSubmit}>
                  <div className="auth-field">
                    <FaEnvelope className="auth-field-icon" />
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      value={form.email}
                      onChange={handleChange}
                      className="auth-input"
                      required
                    />
                  </div>

                  <div className="auth-field">
                    <FaLock className="auth-field-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter your password"
                      value={form.password}
                      onChange={handleChange}
                      className="auth-input"
                      required
                    />
                    <button
                      type="button"
                      className="input-icon-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? "🙈" : "👁️"}
                    </button>
                  </div>

                  <div className="auth-row">
                    <label className="auth-checkbox">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <span>Remember me</span>
                    </label>

                    <Link to="/forgot-password" className="auth-forgot-link">
                      Forgot Password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    className="auth-submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? "Checking credentials..." : "Log In"}
                  </button>
                </form>

                <div className="auth-divider">
                  <span>or</span>
                </div>

                <div className="google-login-wrapper">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError("Google login failed.")}
                    theme="filled_blue"
                    size="large"
                    shape="pill"
                    text="continue_with"
                  />
                </div>

                <p className="auth-footer-text">
                  Don't have an account? <Link to="/signup">Sign Up</Link>
                </p>
              </>
            ) : (
              <>
                <h1 className="auth-title">
                  Verify <span className="auth-accent">Login</span>
                </h1>

                <p className="auth-subtitle">
                  We sent a 6-digit verification code to <strong>{form.email}</strong>
                </p>

                {successMessage && <div className="auth-success">{successMessage}</div>}
                {error && <div className="auth-error">{error}</div>}

                <form onSubmit={handleVerifyOtp}>
                  <div className="fp-otp-row" onPaste={handleOtpPaste}>
                    {otpDigits.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (otpRefs.current[i] = el)}
                        className="fp-otp-input"
                        type="text"
                        maxLength={1}
                        inputMode="numeric"
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        autoFocus={i === 0}
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    className="auth-submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? "Verifying code..." : "Verify & Sign In"}
                  </button>

                  <p className="auth-footer-text">
                    Didn't receive code?{" "}
                    <button
                      type="button"
                      className="fp-link"
                      onClick={handleResend}
                      disabled={resending || resendTimer > 0}
                    >
                      {resending
                        ? "Sending..."
                        : resendTimer > 0
                        ? `Resend in ${resendTimer}s`
                        : "Resend code"}
                    </button>
                  </p>

                  <p className="auth-footer-text" style={{ marginTop: 12 }}>
                    Entered wrong details?{" "}
                    <button
                      type="button"
                      className="fp-link"
                      onClick={() => {
                        setStep(1);
                        setError("");
                      }}
                    >
                      Back to sign in
                    </button>
                  </p>
                </form>
              </>
            )}
          </div>

          {/* RIGHT SIDE */}
          <div className="auth-right">
            <div className="auth-illustration">
              <img
                src={loginImage}
                alt="Login Illustration"
                className="auth-image"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;